import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/auth/AuthContext';
import { supabase } from '../../lib/supabaseClient';
import WalletCard from '../../components/wallet/WalletCard';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import FormStatusBadge from '../../components/forms/FormStatusBadge';

interface Appointment {
  id: string;
  start_time: string;
  end_time: string;
  status: string;
  payment_status: string;
  notes: string | null;
  appointment_services: Array<{
    service_id: string;
    services: {
      name: string;
      duration: number;
      price: number;
    } | null;
  }> | null;
}

interface Receipt {
  id: string;
  receipt_number: string;
  reference_id: string;
}

const ProfilePage: React.FC = () => {
  const { userProfile, user, refreshUserProfile, memberCredits, isMember } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [upcomingAppointments, setUpcomingAppointments] = useState<Appointment[]>([]);
  const [pastAppointments, setPastAppointments] = useState<Appointment[]>([]);
  const [appointmentReceipts, setAppointmentReceipts] = useState<Map<string, Receipt>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    preferred_name: '',
    phone_number: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: ''
  });
  const [saveMessage, setSaveMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isGeneratingPass, setIsGeneratingPass] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCancelMembershipDialog, setShowCancelMembershipDialog] = useState(false);
  const [isCancellingMembership, setIsCancellingMembership] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<{
    cancelAtPeriodEnd: boolean;
    cancelAt: string | null;
    canCancel: boolean;
    cancellationEligibleDate: string | null;
    daysUntilCancellable: number;
    memberSince: string | null;
  } | null>(null);

  // Consent form state
  const [consentForm, setConsentForm] = useState<{
    id: string;
    formToken: string;
    signedAt: string | null;
    signatureUrl: string | null;
  } | null>(null);
  const [isLoadingConsentForm, setIsLoadingConsentForm] = useState(true);

  // Show error message from redirect (e.g., from AdminRoute)
  useEffect(() => {
    const state = location.state as any;
    if (state?.error) {
      setErrorMessage(state.error);
      // Clear the error from location state
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Initialize form data from userProfile
  useEffect(() => {
    if (userProfile) {
      setFormData({
        full_name: userProfile.full_name || '',
        preferred_name: userProfile.preferred_name || '',
        phone_number: userProfile.phone_number || '',
        address_line1: userProfile.address_line1 || '',
        address_line2: userProfile.address_line2 || '',
        city: userProfile.city || '',
        state: userProfile.state || '',
        zip_code: userProfile.zip_code || ''
      });
    }
  }, [userProfile]);

  // Fetch appointments
  useEffect(() => {
    const fetchAppointments = async () => {
      if (!user?.id) return;

      try {
        const { data, error } = await supabase
          .from('appointments')
          .select(`
            id,
            start_time,
            end_time,
            status,
            payment_status,
            notes,
            staff_id,
            team!appointments_staff_id_fkey (
              name,
              role,
              photo
            ),
            appointment_services (
              service_id,
              services (
                name,
                duration,
                price
              )
            )
          `)
          .eq('client_id', user.id)
          .order('start_time', { ascending: true });

        if (error) {
          return;
        }

        const now = new Date();
        const appointments = (data || []) as unknown as Appointment[];

        // Filter out cancelled appointments
        const activeAppointments = appointments.filter(apt => apt.status !== 'cancelled');
        const upcoming = activeAppointments.filter(apt => new Date(apt.start_time) >= now);
        const past = activeAppointments.filter(apt => new Date(apt.start_time) < now);

        setUpcomingAppointments(upcoming);
        setPastAppointments(past);

        // Fetch receipts for paid appointments
        const paidAppointmentIds = activeAppointments
          .filter(apt => apt.payment_status === 'paid')
          .map(apt => apt.id);

        if (paidAppointmentIds.length > 0) {
          const { data: receipts } = await supabase
            .from('receipts')
            .select('id, receipt_number, reference_id')
            .eq('receipt_type', 'appointment')
            .in('reference_id', paidAppointmentIds);

          if (receipts) {
            const receiptMap = new Map<string, Receipt>();
            receipts.forEach(r => receiptMap.set(r.reference_id, r as Receipt));
            setAppointmentReceipts(receiptMap);
          }
        }
      } catch (error) {
        // Silent fail
      } finally {
        setIsLoading(false);
      }
    };

    fetchAppointments();

    // Setup realtime subscription for appointment changes
    if (user?.id) {
      const channel = supabase
        .channel('customer-appointments-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'appointments',
            filter: `client_id=eq.${user.id}`
          },
          (payload) => {
            // Refresh appointments when any change occurs
            fetchAppointments();
          }
        )
        .subscribe();

      // Cleanup subscription on unmount
      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [user]);

  // Fetch subscription status to check if cancellation is pending
  const fetchSubscriptionStatus = async () => {
    if (!user?.id || userProfile?.membership_status !== 'active') {
      setSubscriptionStatus(null);
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/get-subscription-status`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSubscriptionStatus({
          cancelAtPeriodEnd: data.cancelAtPeriodEnd || false,
          cancelAt: data.cancelAt || null,
          canCancel: data.canCancel ?? true,
          cancellationEligibleDate: data.cancellationEligibleDate || null,
          daysUntilCancellable: data.daysUntilCancellable || 0,
          memberSince: data.memberSince || null,
        });
      }
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
    }
  };

  useEffect(() => {
    fetchSubscriptionStatus();
  }, [user, userProfile?.membership_status]);

  // Fetch consent form data
  useEffect(() => {
    const fetchConsentForm = async () => {
      if (!user?.id) {
        setIsLoadingConsentForm(false);
        return;
      }

      // Don't query if account is deleted (prevents 406 errors with anonymized email)
      if (userProfile?.account_status === 'deleted') {
        setIsLoadingConsentForm(false);
        return;
      }

      try {
        // First check if user has a linked consent form
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('facial_consent_form_id')
          .eq('id', user.id)
          .single();

        if (profile?.facial_consent_form_id) {
          // Get form details
          const { data: form } = await supabase
            .from('facial_consent_forms')
            .select('id, form_token, signed_at, signature_url')
            .eq('id', profile.facial_consent_form_id)
            .single();

          if (form) {
            setConsentForm({
              id: form.id,
              formToken: form.form_token,
              signedAt: form.signed_at,
              signatureUrl: form.signature_url,
            });
          }
        } else {
          // Check if there's a form linked by email
          const { data: form } = await supabase
            .from('facial_consent_forms')
            .select('id, form_token, signed_at, signature_url')
            .eq('email', userProfile?.email || user.email)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (form) {
            setConsentForm({
              id: form.id,
              formToken: form.form_token,
              signedAt: form.signed_at,
              signatureUrl: form.signature_url,
            });
          }
        }
      } catch (error) {
        console.error('Error fetching consent form:', error);
      } finally {
        setIsLoadingConsentForm(false);
      }
    };

    fetchConsentForm();
  }, [user, userProfile?.email, userProfile?.account_status]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async () => {
    if (!user?.id) return;

    try {
      const { error } = await supabase
        .from('user_profiles')
        .update(formData)
        .eq('id', user.id);

      if (error) {
        setErrorMessage('Failed to update profile. Please try again.');
        return;
      }

      setSaveMessage('Profile updated successfully!');
      setEditMode(false);
      // Refresh user profile in AuthContext
      await refreshUserProfile();

      // Clear success message after 3 seconds
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      setErrorMessage('An unexpected error occurred.');
    }
  };

  const formatDate = (dateString: string) => {
    // DB stores timestamp without timezone, so add 'Z' to parse as UTC
    // This matches how admin reads appointments
    const utcDateString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    const utcDate = new Date(utcDateString);
    
    // Format using Texas timezone
    return utcDate.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/Chicago'
    });
  };

  // Handle account deletion
  const handleDeleteAccount = async () => {
    setShowDeleteDialog(false);

    try {
      if (!user?.id) {
        setErrorMessage('Not logged in');
        return;
      }

      // Get auth session for API call
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setErrorMessage('Session expired. Please log in again.');
        return;
      }

      // Call Edge Function to properly delete account
      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/delete-user-account`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: user.id,
            deletionReason: 'User requested deletion',
            isAdminDelete: false
          })
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to delete account');
      }

      // Sign out the user on ALL devices (global logout for security)
      await supabase.auth.signOut({ scope: 'global' });

      // Redirect to home
      navigate('/', { replace: true });
    } catch (error: any) {
      console.error('Delete account error:', error);
      setErrorMessage(error.message || 'Failed to delete account. Please try again or contact support.');
    }
  };

  // Handle membership cancellation
  const handleCancelMembership = async () => {
    setShowCancelMembershipDialog(false);
    setIsCancellingMembership(true);
    setErrorMessage('');

    try {
      if (!user?.id) {
        setErrorMessage('Not logged in');
        return;
      }

      // Get auth session for API call
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setErrorMessage('Session expired. Please log in again.');
        return;
      }

      // Call Edge Function to cancel subscription
      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/cancel-subscription`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to cancel membership');
      }

      // Refresh user profile and subscription status
      await refreshUserProfile();
      await fetchSubscriptionStatus();

      // Show success message
      setSaveMessage('Membership will be cancelled at the end of your billing period. You can continue to enjoy member benefits until then.');

      // Clear message after 8 seconds
      setTimeout(() => setSaveMessage(''), 8000);
    } catch (error: any) {
      console.error('Cancel membership error:', error);
      setErrorMessage(error.message || 'Failed to cancel membership. Please try again or contact support.');
    } finally {
      setIsCancellingMembership(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#3A5E63]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F6F2] via-[#E8F5F2] to-[#F9E9E9] py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-serif font-semibold text-[#3A5E63] mb-2">
            Welcome, {userProfile?.preferred_name || userProfile?.full_name || 'Guest'}!
          </h1>
          <p className="text-[#344854]/70">Manage your profile and appointments</p>
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {errorMessage}
            <button onClick={() => setErrorMessage('')} className="ml-4 text-sm underline">
              Dismiss
            </button>
          </div>
        )}

        {/* Success Message */}
        {saveMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
            {saveMessage}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Profile Info & Membership */}
          <div className="lg:col-span-1 space-y-6">
            {/* Profile Information */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-[#3A5E63]">Profile Information</h2>
                {!editMode && (
                  <button
                    onClick={() => setEditMode(true)}
                    className="text-sm text-[#3A5E63] hover:underline"
                  >
                    Edit
                  </button>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#344854] mb-1">
                    Full Name
                  </label>
                  {editMode ? (
                    <input
                      type="text"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-[#B2D8C5] rounded focus:outline-none focus:ring-2 focus:ring-[#3A5E63]"
                    />
                  ) : (
                    <p className="text-[#344854]">{userProfile?.full_name || 'Not set'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#344854] mb-1">
                    Preferred Name
                  </label>
                  {editMode ? (
                    <input
                      type="text"
                      name="preferred_name"
                      value={formData.preferred_name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-[#B2D8C5] rounded focus:outline-none focus:ring-2 focus:ring-[#3A5E63]"
                    />
                  ) : (
                    <p className="text-[#344854]">{userProfile?.preferred_name || 'Not set'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#344854] mb-1">
                    Email
                  </label>
                  <p className="text-[#344854]">
                    {userProfile?.email && !userProfile.email.endsWith('@phone.local')
                      ? userProfile.email
                      : 'Not set'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#344854] mb-1">
                    Phone Number
                  </label>
                  {editMode ? (
                    <input
                      type="tel"
                      name="phone_number"
                      value={formData.phone_number}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-[#B2D8C5] rounded focus:outline-none focus:ring-2 focus:ring-[#3A5E63]"
                    />
                  ) : (
                    <p className="text-[#344854]">{userProfile?.phone_number || 'Not set'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#344854] mb-1">
                    Address
                  </label>
                  {editMode ? (
                    <>
                      <input
                        type="text"
                        name="address_line1"
                        value={formData.address_line1}
                        onChange={handleInputChange}
                        placeholder="Address Line 1"
                        className="w-full px-3 py-2 border border-[#B2D8C5] rounded focus:outline-none focus:ring-2 focus:ring-[#3A5E63] mb-2"
                      />
                      <input
                        type="text"
                        name="address_line2"
                        value={formData.address_line2}
                        onChange={handleInputChange}
                        placeholder="Address Line 2 (optional)"
                        className="w-full px-3 py-2 border border-[#B2D8C5] rounded focus:outline-none focus:ring-2 focus:ring-[#3A5E63] mb-2"
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          placeholder="City"
                          className="px-3 py-2 border border-[#B2D8C5] rounded focus:outline-none focus:ring-2 focus:ring-[#3A5E63]"
                        />
                        <input
                          type="text"
                          name="state"
                          value={formData.state}
                          onChange={handleInputChange}
                          placeholder="State"
                          className="px-3 py-2 border border-[#B2D8C5] rounded focus:outline-none focus:ring-2 focus:ring-[#3A5E63]"
                        />
                      </div>
                      <input
                        type="text"
                        name="zip_code"
                        value={formData.zip_code}
                        onChange={handleInputChange}
                        placeholder="ZIP Code"
                        className="w-full px-3 py-2 border border-[#B2D8C5] rounded focus:outline-none focus:ring-2 focus:ring-[#3A5E63] mt-2"
                      />
                    </>
                  ) : (
                    <div className="text-[#344854]">
                      {userProfile?.address_line1 ? (
                        <>
                          <p>{userProfile.address_line1}</p>
                          {userProfile.address_line2 && <p>{userProfile.address_line2}</p>}
                          <p>
                            {userProfile.city}, {userProfile.state} {userProfile.zip_code}
                          </p>
                        </>
                      ) : (
                        <p>Not set</p>
                      )}
                    </div>
                  )}
                </div>

                {editMode && (
                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={handleSaveProfile}
                      className="flex-1 bg-[#3A5E63] text-white py-2 rounded hover:bg-[#2d4a4d] transition-colors"
                    >
                      Save Changes
                    </button>
                    <button
                      onClick={() => {
                        setEditMode(false);
                        // Reset form data
                        setFormData({
                          full_name: userProfile?.full_name || '',
                          preferred_name: userProfile?.preferred_name || '',
                          phone_number: userProfile?.phone_number || '',
                          address_line1: userProfile?.address_line1 || '',
                          address_line2: userProfile?.address_line2 || '',
                          city: userProfile?.city || '',
                          state: userProfile?.state || '',
                          zip_code: userProfile?.zip_code || ''
                        });
                      }}
                      className="px-4 py-2 border border-[#B2D8C5] text-[#344854] rounded hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Membership Status */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-[#3A5E63] mb-4">Membership Status</h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-[#344854]">Status:</span>
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      userProfile?.membership_status === 'active'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {userProfile?.membership_status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {userProfile?.membership_status === 'active' && (
                  <>
                    {/* Show Member Since date */}
                    {subscriptionStatus?.memberSince && (
                      <p className="text-sm text-[#344854]/70">
                        Member since{' '}
                        {new Date(subscriptionStatus.memberSince).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </p>
                    )}

                    {/* Show cancellation notice if pending */}
                    {subscriptionStatus?.cancelAtPeriodEnd && subscriptionStatus.cancelAt && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <p className="text-sm text-yellow-800 font-medium">
                          Membership will be cancelled on{' '}
                          {new Date(subscriptionStatus.cancelAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </p>
                        <p className="text-xs text-yellow-700 mt-1">
                          You'll continue to enjoy 20% off until then.
                        </p>
                      </div>
                    )}

                    {/* Show commitment period notice if not yet eligible to cancel */}
                    {!subscriptionStatus?.cancelAtPeriodEnd && subscriptionStatus?.canCancel === false && subscriptionStatus.cancellationEligibleDate && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-sm text-blue-800 font-medium">
                          2-Month Commitment Period
                        </p>
                        <p className="text-xs text-blue-700 mt-1">
                          Cancellation available after{' '}
                          {new Date(subscriptionStatus.cancellationEligibleDate).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                          {' '}({subscriptionStatus.daysUntilCancellable} days remaining)
                        </p>
                      </div>
                    )}

                    {/* Show discount message if not pending cancellation */}
                    {!subscriptionStatus?.cancelAtPeriodEnd && (
                      <p className="text-sm text-green-600">
                        You get 20% off all services!
                      </p>
                    )}

                    {/* Show "Undo Cancellation" if pending */}
                    {subscriptionStatus?.cancelAtPeriodEnd && (
                      <button
                        onClick={async () => {
                          setIsCancellingMembership(true);
                          setErrorMessage('');
                          try {
                            const { data: { session } } = await supabase.auth.getSession();
                            if (!session?.access_token) {
                              setErrorMessage('Session expired. Please log in again.');
                              return;
                            }

                            const response = await fetch(
                              `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/reactivate-subscription`,
                              {
                                method: 'POST',
                                headers: {
                                  'Authorization': `Bearer ${session.access_token}`,
                                  'Content-Type': 'application/json',
                                },
                              }
                            );

                            const result = await response.json();

                            if (!response.ok || !result.success) {
                              throw new Error(result.error || 'Failed to reactivate membership');
                            }

                            // Refresh subscription status
                            await fetchSubscriptionStatus();

                            // Show success message
                            setSaveMessage('Membership reactivated successfully! Your subscription will continue.');
                            setTimeout(() => setSaveMessage(''), 5000);
                          } catch (error: any) {
                            console.error('Reactivate membership error:', error);
                            setErrorMessage(error.message || 'Failed to reactivate membership.');
                          } finally {
                            setIsCancellingMembership(false);
                          }
                        }}
                        disabled={isCancellingMembership}
                        className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isCancellingMembership ? 'Reactivating...' : 'Undo Cancellation'}
                      </button>
                    )}

                    {/* Show "Cancel Membership" only if eligible (past commitment period) and not already pending */}
                    {!subscriptionStatus?.cancelAtPeriodEnd && subscriptionStatus?.canCancel && (
                      <button
                        onClick={() => setShowCancelMembershipDialog(true)}
                        disabled={isCancellingMembership}
                        className="w-full bg-red-600 text-white py-2 rounded hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isCancellingMembership ? 'Cancelling...' : 'Cancel Membership'}
                      </button>
                    )}
                  </>
                )}

                {userProfile?.membership_status !== 'active' && (
                  <button
                    onClick={() => navigate('/join')}
                    className="w-full bg-[#B2D8C5] text-white py-2 rounded hover:bg-[#88bfa3] transition-colors"
                  >
                    Join Membership
                  </button>
                )}
              </div>
            </div>

            {/* Free Service Credits - Only show for active members with credits */}
            {isMember && memberCredits.length > 0 && (
              <div className="bg-gradient-to-r from-teal-50 to-amber-50 rounded-lg shadow-md p-6 border border-teal-200">
                <div className="flex items-center gap-2 mb-4">
                  <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h2 className="text-xl font-semibold text-teal-800">Free Service Credits</h2>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  As a VIP member, you receive complimentary services each month!
                </p>
                <div className="space-y-3">
                  {memberCredits.map((credit) => (
                    <div
                      key={credit.serviceId}
                      className="bg-white rounded-lg p-3 border border-teal-100"
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-gray-800">{credit.serviceName}</span>
                        <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                          credit.quantity > 0
                            ? 'bg-teal-100 text-teal-700'
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          {credit.quantity} available
                        </span>
                      </div>
                      <div className="mt-1 flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-teal-500 rounded-full h-2 transition-all duration-300"
                            style={{ width: `${Math.min((credit.quantity / credit.maxQuantity) * 100, 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500">
                          max {credit.maxQuantity}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-4">
                  Credits are added monthly and can accumulate up to their maximum. Use them when booking!
                </p>
              </div>
            )}

            {/* Facial Consent Form */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-[#3A5E63] mb-4">Facial Consent Form</h2>
              {isLoadingConsentForm ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#3A5E63]"></div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-[#344854]">Status:</span>
                    <FormStatusBadge isComplete={!!consentForm?.signedAt} />
                  </div>

                  {consentForm?.signedAt && (
                    <p className="text-sm text-[#344854]/70">
                      Signed on{' '}
                      {new Date(consentForm.signedAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </p>
                  )}

                  <p className="text-sm text-[#344854]/70">
                    {consentForm?.signedAt
                      ? 'You can update your information (signature remains locked).'
                      : 'Required before your first facial treatment.'}
                  </p>

                  <button
                    onClick={() => {
                      if (consentForm?.formToken) {
                        navigate(`/consent-form?token=${consentForm.formToken}`);
                      } else {
                        // Navigate to consent form page without token - will create new form for logged in user
                        navigate('/consent-form');
                      }
                    }}
                    className="w-full bg-[#3A5E63] text-white py-2 rounded hover:bg-[#2d4a4d] transition-colors flex items-center justify-center gap-2"
                  >
                    {consentForm?.signedAt ? (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                          <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                        </svg>
                        View / Update Form
                      </>
                    ) : (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                        Fill Out Form
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* Wallet */}
            <WalletCard />

            {/* Apple Wallet */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-[#3A5E63] mb-4">Digital Wallet Pass</h2>
              <p className="text-sm text-[#344854]/70 mb-4">
                Add your membership card to Apple Wallet for quick access to your balance and member benefits.
              </p>
              <button
                onClick={async () => {
                  if (isGeneratingPass) return;

                  setIsGeneratingPass(true);
                  setErrorMessage('');

                  try {
                    const { data: { session } } = await supabase.auth.getSession();
                    if (!session?.access_token) {
                      setErrorMessage('Please log in to download your wallet pass');
                      return;
                    }

                    const response = await fetch(
                      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/generate-wallet-pass`,
                      {
                        method: 'POST',
                        headers: {
                          'Authorization': `Bearer ${session.access_token}`,
                          'Content-Type': 'application/json',
                        },
                      }
                    );

                    const data = await response.json();

                    if (!response.ok) {
                      throw new Error(data.error || 'Failed to get wallet pass');
                    }

                    if (data.hasPass && data.passUrl) {
                      // Navigate directly to the pass URL
                      // On iOS, this triggers the "Add to Apple Wallet" prompt
                      // On desktop, this downloads the .pkpass file
                      window.location.href = data.passUrl;
                    } else {
                      // Pass not yet generated
                      setErrorMessage(data.message || 'Your wallet pass is being prepared. Please try again in a few minutes.');
                    }
                  } catch (error: any) {
                    setErrorMessage(error.message || 'Failed to download wallet pass');
                  } finally {
                    setIsGeneratingPass(false);
                  }
                }}
                disabled={isGeneratingPass}
                className={`w-full bg-black text-white py-3 rounded-lg transition-colors flex items-center justify-center gap-2 ${
                  isGeneratingPass ? 'opacity-70 cursor-not-allowed' : 'hover:bg-gray-800'
                }`}
              >
                {isGeneratingPass ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating Pass...
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                    </svg>
                    Add to Apple Wallet
                  </>
                )}
              </button>
              <p className="text-xs text-[#344854]/50 mt-2 text-center">
                Works on iPhone and Apple Watch
              </p>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-[#3A5E63] mb-4">Quick Actions</h2>
              <div className="space-y-2">
                <button
                  onClick={() => navigate('/booking')}
                  className="w-full bg-[#3A5E63] text-white py-2 rounded hover:bg-[#2d4a4d] transition-colors"
                >
                  Book Appointment
                </button>
                <button
                  onClick={() => navigate('/services')}
                  className="w-full border border-[#B2D8C5] text-[#344854] py-2 rounded hover:bg-gray-50 transition-colors"
                >
                  View Services
                </button>
              </div>
            </div>

            {/* Account Settings */}
            <div className="bg-white rounded-lg shadow-md p-6 border-2 border-red-100">
              <h2 className="text-xl font-semibold text-red-600 mb-4">Account Settings</h2>
              <p className="text-sm text-[#344854]/70 mb-4">
                Deleting your account will permanently remove your profile information. Your appointment history and payment records will be preserved but anonymized.
              </p>
              <button
                onClick={() => setShowDeleteDialog(true)}
                className="w-full bg-red-600 text-white py-2 rounded hover:bg-red-700 transition-colors font-medium"
              >
                Delete My Account
              </button>
            </div>
          </div>

          {/* Right Column - Appointments */}
          <div className="lg:col-span-2 space-y-6">
            {/* Upcoming Appointments */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-[#3A5E63] mb-4">Upcoming Appointments</h2>
              {upcomingAppointments.length === 0 ? (
                <div className="text-center py-8 text-[#344854]/70">
                  <p className="mb-4">No upcoming appointments</p>
                  <button
                    onClick={() => navigate('/booking')}
                    className="bg-[#3A5E63] text-white px-6 py-2 rounded hover:bg-[#2d4a4d] transition-colors"
                  >
                    Book Now
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {upcomingAppointments.map((apt) => (
                    <div
                      key={apt.id}
                      className="border border-[#B2D8C5] rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-medium text-[#3A5E63]">
                            {formatDate(apt.start_time)}
                          </p>
                          {/* @ts-ignore - team relation */}
                          {apt.team && (
                            <p className="text-sm text-[#344854] mt-1 font-medium">
                              {/* @ts-ignore */}
                              with {apt.team.name}
                            </p>
                          )}
                          <div className="text-sm text-[#344854]/70 mt-1">
                            {apt.appointment_services && apt.appointment_services.map((as, idx) => (
                              <span key={idx}>
                                {as.services?.name}
                                {idx < (apt.appointment_services?.length || 0) - 1 && ', '}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-medium ${
                              apt.payment_status === 'paid'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-yellow-100 text-yellow-700'
                            }`}
                          >
                            {apt.payment_status === 'paid' ? 'Paid' : 'Payment Pending'}
                          </span>
                          {apt.payment_status === 'paid' && appointmentReceipts.has(apt.id) && (
                            <Link
                              to={`/receipt/${appointmentReceipts.get(apt.id)?.id}`}
                              className="text-xs text-[#3A5E63] hover:underline flex items-center gap-1"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              View Receipt
                            </Link>
                          )}
                        </div>
                      </div>
                      {apt.notes && (
                        <p className="text-sm text-[#344854]/70 mt-2">Note: {apt.notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Past Appointments */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-[#3A5E63] mb-4">Appointment History</h2>
              {pastAppointments.length === 0 ? (
                <p className="text-center py-8 text-[#344854]/70">No past appointments</p>
              ) : (
                <div className="space-y-3">
                  {pastAppointments.slice(0, 5).map((apt) => (
                    <div
                      key={apt.id}
                      className="border border-gray-200 rounded-lg p-3 bg-gray-50"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-sm font-medium text-[#344854]">
                            {formatDate(apt.start_time)}
                          </p>
                          <p className="text-xs text-[#344854]/70 mt-1">
                            {apt.appointment_services && apt.appointment_services.map((as, idx) => (
                              <span key={idx}>
                                {as.services?.name}
                                {idx < (apt.appointment_services?.length || 0) - 1 && ', '}
                              </span>
                            ))}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className="text-xs text-[#344854]/70">
                            {apt.status}
                          </span>
                          {apt.payment_status === 'paid' && appointmentReceipts.has(apt.id) && (
                            <Link
                              to={`/receipt/${appointmentReceipts.get(apt.id)?.id}`}
                              className="text-xs text-[#3A5E63] hover:underline flex items-center gap-1"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              Receipt
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {pastAppointments.length > 5 && (
                    <p className="text-sm text-center text-[#344854]/70 pt-2">
                      Showing 5 most recent
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Cancel Membership Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showCancelMembershipDialog}
        title="Cancel Your Membership?"
        message={[
          'This will:',
          '• Remove your 20% discount on all services',
          '• Cancel automatic monthly renewal',
          '',
          'Your membership benefits will continue until',
          'the end of your current billing period.',
          '',
          'You can rejoin anytime.'
        ]}
        confirmText="Cancel Membership"
        cancelText="Keep Membership"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        onConfirm={handleCancelMembership}
        onCancel={() => setShowCancelMembershipDialog(false)}
      />

      {/* Delete Account Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Delete Your Account?"
        message={[
          'This will permanently delete:',
          '• Your profile and personal information',
          '• Your wallet balance',
          '• Your membership status',
          '',
          'This action cannot be undone.'
        ]}
        confirmText="Delete Account"
        cancelText="Cancel"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        onConfirm={handleDeleteAccount}
        onCancel={() => setShowDeleteDialog(false)}
      />
    </div>
  );
};

export default ProfilePage;
